/**
 * useFinopsScope — deriva o perfil/scope FinOps do caller (read-only).
 *
 * NÃO usa `useScope` (domínio de chamados). O perfil FinOps é decidido por
 * `is_admin` / permissão `admin:platform`, espelhando o gate de plataforma do
 * backend (SP0 §5.1, spec §3.1/§5.6):
 *
 *   - admin global  → `<PlatformHome>` (vê o agregado de plataforma; o backend
 *     poda para não-admin, então admin enxerga tudo).
 *   - gestor escopado → `<ManagerHome>` (só as contas em scope.account_ids).
 *
 * Opcionalmente, expõe `scopeReason` lido de `/my-portfolio` (passado pelo
 * caller que já fez o fetch do portfólio) para mensagens distintas de acesso
 * (`no_access` / `tenant_without_account`, §3.1). O hook NÃO faz fetch próprio
 * — ele apenas interpreta o sinal que o caller fornece, mantendo-se read-only.
 */
import { useCan, useAuthStore } from '@/store/auth'
import type { ScopeReason } from '@/api/finopsDashboard'

export type FinopsProfile = 'platform' | 'manager'

export interface UseFinopsScopeReturn {
  /** Perfil resolvido: 'platform' (admin) ou 'manager' (gestor escopado). */
  profile: FinopsProfile
  /** true se o caller é admin de plataforma (is_admin OU admin:platform). */
  isPlatformAdmin: boolean
  /**
   * scope_reason vindo de /my-portfolio (quando o caller o repassa).
   * undefined enquanto o portfólio não foi resolvido.
   */
  scopeReason?: ScopeReason
  /** Mensagem de acesso pronta para exibição, ou null se há acesso normal. */
  accessMessage: string | null
}

const ACCESS_MESSAGES: Partial<Record<ScopeReason, string>> = {
  no_access: 'Você não tem acesso a custos.',
  tenant_without_account:
    'Sua organização ainda não tem conta AWS vinculada.',
}

/**
 * @param scopeReason scope_reason de /my-portfolio (opcional; o caller que já
 *   tem o portfólio repassa para derivar a mensagem de acesso).
 */
export function useFinopsScope(scopeReason?: ScopeReason): UseFinopsScopeReturn {
  const can = useCan()
  const user = useAuthStore((s) => s.user)

  const isPlatformAdmin = Boolean(user?.is_admin) || can('admin', 'platform')

  const accessMessage = scopeReason
    ? ACCESS_MESSAGES[scopeReason] ?? null
    : null

  return {
    profile: isPlatformAdmin ? 'platform' : 'manager',
    isPlatformAdmin,
    scopeReason,
    accessMessage,
  }
}
